/* Copyright (c) 2017 Backpack Health LLC */

export enum OverlayBlur {
  darkSmallBlur = 'darkSmallBlur',
  darkBigBlur = 'darkBigBlur',
  lightSmallBlur = 'lightSmallBlur',
  inherit = '',
}

// TYPES

const types = {
  LOAD: 'app/systemModal/LOAD',
  SHOW: 'app/systemModal/SHOW',
  DISMISS: 'app/systemModal/DISMISS',
  PUSH: 'app/systemModal/PUSH',
  POP: 'app/systemModal/POP',
  DISMISS_ALL: 'app/systemModal/DISMISS_ALL',
  TOGGLE_APP_MENU: 'app/systemModal/TOGGLE_APP_MENU',
  UPDATE: 'app/systemModal/UPDATE',
};

const basicData = {
  content: null,
  isWithSliding: false,
  isLoaded: false,
  onDismiss: null,
  isBackgroundClickDisabled: false,
  blurSize: null,
  isAutoDismissDisabled: false,
};

const initialState = {
  activeModals: [],
  appMenu: {
    isVisible: false,
  },
};

// REDUCER

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case types.LOAD:
      return {
        ...state,
        activeModals: state.activeModals.map((c, i) => {
          if (i === (state.activeModals.length - 1)) {
            c.isLoaded = true;
          }
          return c;
        }),
      };
    case types.SHOW:
      return {
        ...state,
        activeModals: [action.payload],
      };
    case types.PUSH:
      return {
        ...state,
        activeModals: state.activeModals.concat([action.payload]),
      };
    case types.POP:
    case types.DISMISS:
      return {
        ...state,
        activeModals: state.activeModals.slice(0, state.activeModals.length - 1),
      };
    case types.DISMISS_ALL:
      return {
        ...state,
        activeModals: [],
      };
    case types.TOGGLE_APP_MENU:
      return {
        ...state,
        appMenu: action.payload,
      };
    case types.UPDATE:
      return {
        ...state,
        ...action.payload,
      };
    default:
      return state;
  }
}

// SELECTORS

const getActiveModal = (state) => {
  const contentLength = state.systemModal.activeModals.length;
  return contentLength > 0 ? state.systemModal.activeModals[contentLength - 1] : null;
};

export const selectors = {
  isVisible: state => state.systemModal.activeModals.length > 0,
  isWithSliding: state => {
    const activeModal = getActiveModal(state);
    return activeModal ? activeModal.isWithSliding : false;
  },
  isLoaded: state => {
    const activeModal = getActiveModal(state);
    return activeModal ? activeModal.isLoaded : false;
  },
  getContent: state => {
    const activeModal = getActiveModal(state);
    return activeModal ? activeModal.content : null;
  },
  getAllContent: state => state.systemModal.activeModals.map(m => m.content),
  getOnDismiss: state => {
    const activeModal = getActiveModal(state);
    return activeModal ? activeModal.onDismiss : null;
  },
  isBackgroundClickDisabled: state => {
    const activeModal = getActiveModal(state);
    return activeModal ? activeModal.isBackgroundClickDisabled : null;
  },
  getBlurSize: state => {
    const { activeModals } = state.systemModal;
    for (let i = activeModals.length - 1; i > -1; i--) {
      if (activeModals[i].blurSize) {
        return activeModals[i].blurSize;
      }
    }
    return '';
  },
  getIsAppMenuVisible: state => state.systemModal.appMenu.isVisible,
  getIsAutoDismissDisabled: state => {
    const activeModal = getActiveModal(state);
    return activeModal ? activeModal.isAutoDismissDisabled : false;
  },
};

// ACTION CREATORS

const addModal = (content, options: any = {}) => {
  const blurSize = typeof options.blurSize === 'string' ? options.blurSize : OverlayBlur.darkSmallBlur;
  return {
    ...basicData,
    blurSize,
    content,
    isWithSliding: options.isWithSliding,
    isLoaded: false,
    onDismiss: options.onDismiss,
    isAutoDismissDisabled: !!options.isAutoDismissDisabled,
  };
};

export const actions = {

  load: () => ({
    type: types.LOAD,
    payload: {},
  }),

  showNotLoaded: (content, options = {}) => {
    return {
      type: types.SHOW,
      payload: {
        ...addModal(content, options),
      },
    };
  },

  show: (content, options = {}) => {
    return {
      type: types.SHOW,
      payload: {
        ...addModal(content, options),
        isLoaded: true,
      },
    };
  },

  dismiss: () => ({
    type: types.DISMISS,
    payload: {},
  }),

  push: (content, options = {}) => {
    return {
      type: types.PUSH,
      payload: {
        ...addModal(content, options),
        isLoaded: true,
      },
    };
  },

  pop: () => ({
    type: types.POP,
    payload: {},
  }),

  dismissAll: () => ({
    type: types.DISMISS_ALL,
    payload: {},
  }),

  pushDarkBigBlur: (content, options = {}) => {
    return {
      type: types.PUSH,
      payload: {
        ...addModal(content, options),
        isLoaded: true,
        blurSize: OverlayBlur.darkBigBlur,
      },
    };
  },

  pushLightSmallBlur: (content, options = {}) => {
    return {
      type: types.PUSH,
      payload: {
        ...addModal(content, options),
        isLoaded: true,
        blurSize: OverlayBlur.lightSmallBlur,
      },
    };
  },

  setBlur: (blurSize, options = {}) => {
    return {
      type: types.SHOW,
      payload: {
        ...addModal(null, options),
        isLoaded: true,
        blurSize,
        isBackgroundClickDisabled: false,
      },
    };
  },

  removeBlur: () => {
    return {
      type: types.DISMISS,
      payload: {},
    };
  },

  showDarkBigBlur: (content, options = {}) => {
    return {
      type: types.SHOW,
      payload: {
        ...addModal(content, options),
        isLoaded: true,
        blurSize: OverlayBlur.darkBigBlur,
        isBackgroundClickDisabled: true,
      },
    };
  },

  showLightSmallBlur: (content, options = {}) => {
    return {
      type: types.SHOW,
      payload: {
        ...addModal(content, options),
        isLoaded: true,
        blurSize: OverlayBlur.lightSmallBlur,
      },
    };
  },

  showAppMenu: () => {
    return {
      type: types.TOGGLE_APP_MENU,
      payload: {
        isVisible: true,
      },
    };
  },

  hideAppMenu: () => {
    return {
      type: types.TOGGLE_APP_MENU,
      payload: {
        isVisible: false,
      },
    };
  },

  showModalProgressIndicator: (content, options) => {
    return {
      type: types.SHOW,
      payload: {
        ...addModal(content, options),
        isLoaded: true,
        blurSize: OverlayBlur.lightSmallBlur,
      },
    };
  },

  update: (options) => {
    return {
      type: types.UPDATE,
      payload: {
        ...options,
      },
    };
  }
};

// THUNK ACTION CREATORS
